// ─────────────────────────────────────────
// ts/utils.ts — General utility functions
// Tauvior Landing Page
// ─────────────────────────────────────────

import type { ScrollOptions } from './types';

// ── Scroll ────────────────────────────────

/** Folga sob o nav fixo (60px de logo + padding ≈ 100px no topo, ≈ 80px rolado). Mantém igual ao `scroll-margin-top` de home-v2.css. */
const SCROLL_OFFSET = 96;

/**
 * Smoothly scrolls to a page section by element ID.
 * Accounts for the fixed nav height offset.
 */
export function scrollToSection({
  targetId,
  offset = SCROLL_OFFSET,
  behavior = 'smooth',
}: ScrollOptions): void {
  const target = document.getElementById(targetId);
  if (!target) {
    console.warn(`[scrollToSection] Element #${targetId} not found.`);
    return;
  }
  const top = target.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top, behavior: prefersReducedMotion() ? 'auto' : behavior });
}

// ── Math ──────────────────────────────────

/**
 * Clamps a number between min and max.
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Linear interpolation between two values.
 */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * clamp(t, 0, 1);
}

// ── Functions ─────────────────────────────

/**
 * Returns a debounced version of a function.
 * The inner function is only called after `delay` ms of silence.
 */
export function debounce<T extends (...args: unknown[]) => void>(
  fn: T,
  delay: number,
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
}

/**
 * Returns a throttled version of a function.
 * The inner function fires at most once every `limit` ms.
 */
export function throttle<T extends (...args: unknown[]) => void>(
  fn: T,
  limit: number,
): (...args: Parameters<T>) => void {
  let lastCall = 0;
  return (...args: Parameters<T>) => {
    const now = Date.now();
    if (now - lastCall >= limit) {
      lastCall = now;
      fn(...args);
    }
  };
}

// ── DOM ───────────────────────────────────

/**
 * Selects a single DOM element or throws if not found.
 */
export function getElement<T extends HTMLElement>(selector: string): T {
  const el = document.querySelector<T>(selector);
  if (!el) throw new Error(`[getElement] Not found: "${selector}"`);
  return el;
}

/**
 * Selects all DOM elements matching a selector.
 */
export function getElements<T extends HTMLElement>(selector: string): T[] {
  return Array.from(document.querySelectorAll<T>(selector));
}

/**
 * Traps keyboard focus inside a given element (for modals, menus).
 * Returns a cleanup function that removes the event listener.
 *
 * Usage:
 *   const release = trapFocus(modalEl);
 *   // later:
 *   release();
 */
export function trapFocus(container: HTMLElement): () => void {
  const focusable = Array.from(
    container.querySelectorAll<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), ' +
      'textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
    ),
  );

  if (focusable.length === 0) return () => undefined;

  const first = focusable[0];
  const last  = focusable[focusable.length - 1];

  function handler(e: KeyboardEvent): void {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault();
        last.focus();
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  }

  container.addEventListener('keydown', handler);
  first.focus();

  return () => container.removeEventListener('keydown', handler);
}

/**
 * Returns true when the user has prefers-reduced-motion enabled.
 */
export function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Formats a number with locale-aware thousands separators.
 * e.g. formatNumber(1234567, 'pt-BR') → '1.234.567'
 */
export function formatNumber(n: number, locale: string): string {
  return new Intl.NumberFormat(locale).format(n);
}
